import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { AppDispatch, RootState } from "app/store";
import { renderActions } from "features/render/renderSlice";
import { Picker } from "features/render/types";

import { markersActions, selectAllCollections, selectCreationCollectionId } from "./markersSlice";
import { MarkerCollection } from "./types";

/**
 * Centralized logic for stopping/cancelling marker creation flow.
 * Handles Redux state updates, picker reset, and callback invocation.
 *
 * @param dispatch - Redux dispatch function
 * @param getState - Function to get current Redux state
 */
export function stopMarkerCreation(
    dispatch: AppDispatch,
    creationCollectionId: string | null,
    collections: Record<string, MarkerCollection>,
): void {
    // Get the creation config before stopping (to access callbacks)
    const creationConfig = creationCollectionId ? collections[creationCollectionId]?.markerCreationConfig : undefined;

    // Stop marker creation in Redux
    dispatch(markersActions.stopCreateMarker());

    // Reset picker to default
    dispatch(renderActions.stopPicker(Picker.Pins));

    // Trigger onCancelled callback if it exists
    creationConfig?.onCancelled?.();
}

/**
 * React hook that provides a callback to stop marker creation.
 * This is a convenience wrapper around stopMarkerCreation for React components.
 *
 * @returns Memoized function to stop marker creation
 */
export function useStopMarkerCreation() {
    const dispatch = useAppDispatch();
    const creationCollectionId = useAppSelector(selectCreationCollectionId);
    const collections = useAppSelector(selectAllCollections);

    return useCallback(() => {
        stopMarkerCreation(dispatch, creationCollectionId, collections);
    }, [dispatch, creationCollectionId, collections]);
}

/**
 * Selector that determines if the viewer UI should be hidden during marker creation.
 * Checks both the picker state and the hideViewerUI config option.
 *
 * @param state - Redux root state
 * @returns true if UI should be hidden (default: true when creating pins), false otherwise
 */
export function selectNonMarkerUIIsHidden(state: RootState): boolean {
    const picker = state.render.picker;
    const creationCollectionId = state.markers.creationCollectionId;

    // Only check hideViewerUI if we're in pins creation mode
    if (picker !== Picker.Pins || !creationCollectionId) {
        return false;
    }

    const creationConfig = state.markers.collections[creationCollectionId]?.markerCreationConfig;
    // Default to true (hide UI) if not explicitly set to false
    return creationConfig?.hideViewerUI ?? true;
}

/**
 * React hook that returns whether the viewer UI should be hidden.
 * Useful for components that need to conditionally render based on marker creation state.
 *
 * @returns true if UI should be hidden (default: true when creating pins), false otherwise
 */
export function useNonMarkerUIIsHidden(): boolean {
    return useAppSelector(selectNonMarkerUIIsHidden);
}
